import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { PosterSettings, FrameType } from '@/types/canvas';
import { cn } from '@/lib/utils';
import { getAllVariants } from '@/lib/variants';

interface PricingSectionProps {
  posterSettings: PosterSettings;
  onPosterSettingsChange: (settings: Partial<PosterSettings>) => void;
  onCheckout: () => void;
  onAddToCart?: () => void;
}

interface PricingOption {
  size: 'A2' | 'B2';
  dimensions: string;
  frame: false | 'hanger' | true;
  price: number;
  basePrice: number;
  popular: boolean;
  label: string;
  badge?: string;
  features: string[];
  image: string;
}

// Fallback ceny pro případ, že API selže
const FALLBACK_PRICES = {
  a2_poster: 29.90,
  a2_hanger: 14.90,
  b2_poster: 39.90,
  b2_frame: 39.90,
};

export function PricingSection({ posterSettings, onPosterSettingsChange, onCheckout: _onCheckout, onAddToCart }: PricingSectionProps) {
  const { t } = useTranslation();
  const [pricing, setPricing] = useState<PricingOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Načíst ceny z Supabase
  useEffect(() => {
    async function loadPrices() {
      try {
        setLoading(true);
        // Načíst varianty pro A2 i B2
        const [a2Variants, b2Variants] = await Promise.all([
          getAllVariants({ size: 'A2', onlyAvailable: false }),
          getAllVariants({ size: 'B2', onlyAvailable: false })
        ]);

        // 1. A2 Poster Only
        const a2PosterVariant = a2Variants.find(v => v.frame_type === 'none');
        const a2PosterPrice = a2PosterVariant?.price_cents ? a2PosterVariant.price_cents / 100 : FALLBACK_PRICES.a2_poster;

        // 2. A2 + Hanger
        // Hanger is usually a separate product, price is sum of poster + level
        const a2HangerVariant = a2Variants.find(v => v.frame_type === 'hanger' && v.frame_color === 'pine');
        const a2HangerPrice = a2HangerVariant?.price_cents ? a2HangerVariant.price_cents / 100 : FALLBACK_PRICES.a2_hanger;
        const a2FullHangerPrice = a2PosterPrice + a2HangerPrice;

        // 3. B2 + Frame
        const b2PosterVariant = b2Variants.find(v => v.frame_type === 'none');
        const b2FrameVariant = b2Variants.find(v => v.frame_type === 'frame' && v.frame_color === 'pine');
        const b2PosterPrice = b2PosterVariant?.price_cents ? b2PosterVariant.price_cents / 100 : FALLBACK_PRICES.b2_poster;
        const b2FramePrice = b2FrameVariant?.price_cents ? b2FrameVariant.price_cents / 100 : FALLBACK_PRICES.b2_frame;
        const b2FullFramePrice = b2PosterPrice + b2FramePrice;

        const pricingOptions: PricingOption[] = [
          {
            size: 'A2',
            dimensions: '42×59 cm',
            frame: false,
            price: a2PosterPrice,
            basePrice: Math.round(a2PosterPrice / 0.7 * 100) / 100,
            popular: false,
            label: t('pricing.a2Poster', { defaultValue: 'A2 Poster' }),
            badge: t('pricing.a2PosterAffordable', { defaultValue: 'Affordable' }),
            features: [
              t('pricing.paper300mg', { defaultValue: '300mg art paper' }),
              t('pricing.postalTube', { defaultValue: 'Zasíláno v postal tube' }),
              t('pricing.readyAsGift', { defaultValue: 'Ready as gift' })
            ],
            image: '/materials/pricing-poster.jpg'
          },
          {
            size: 'A2',
            dimensions: '42×59 cm',
            frame: 'hanger',
            price: a2FullHangerPrice,
            basePrice: Math.round(a2FullHangerPrice / 0.7 * 100) / 100,
            popular: true,
            label: t('pricing.a2PosterHanger', { defaultValue: 'A2 Poster & Hanger' }),
            badge: t('pricing.a2PosterHangerFull', { defaultValue: 'Full Package' }),
            features: [
              t('pricing.paper300mg', { defaultValue: '300mg art paper' }),
              t('pricing.postalTube', { defaultValue: 'Zasíláno v postal tube' }),
              t('pricing.readyAsGift', { defaultValue: 'Ready as gift' }),
              t('pricing.magneticHanger', { defaultValue: 'Magnetický hanger' })
            ],
            image: '/materials/pricing-hanger-v2.jpg'
          },
          {
            size: 'B2',
            dimensions: '50×70 cm',
            frame: true,
            price: b2FullFramePrice,
            basePrice: Math.round(b2FullFramePrice / 0.7 * 100) / 100,
            popular: false,
            label: t('pricing.b2PosterFrame', { defaultValue: 'B2 Poster & Frame' }),
            badge: t('pricing.b2PosterFramePremium', { defaultValue: 'Premium Package' }),
            features: [
              t('pricing.paper400mg', { defaultValue: '400mg fine art paper' }),
              t('pricing.boxed', { defaultValue: 'Zasíláno v krabici' }),
              t('pricing.insertedInFrame', { defaultValue: 'Plakát vložen do rámu' }),
              t('pricing.readyAsBestGift', { defaultValue: 'Ready as best gift' })
            ],
            image: '/materials/pricing-frame-v2.jpg'
          },
        ];

        setPricing(pricingOptions);
      } catch (error) {
        console.error('[PricingSection] Error loading prices:', error);
        // Fallback flow would go here but relying on try/catch inside getAllVariants returning empty arrays usually
      } finally {
        setLoading(false);
      }
    }

    loadPrices();
  }, [t]);

  if (loading || pricing.length === 0) {
    return (
      <section className="bg-transparent py-8 sm:py-12 lg:py-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-2 sm:mb-4 text-white font-serif">
            {t('pricing.chooseSet', { defaultValue: 'Vyberte si set' })}
          </h2>
          <div className="flex justify-center items-center min-h-[200px]">
            <p className="text-boho-beige/60">{t('common.loading', { defaultValue: 'Načítání...' })}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-transparent py-8 sm:py-12 lg:py-24">
      <div className="w-full pl-4 md:pl-8 lg:pl-12">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-left mb-2 sm:mb-4 text-white font-serif">
          {t('pricing.chooseSet', { defaultValue: 'Vyberte si set' })}
        </h2>
        <p className="text-left text-boho-beige/70 mb-8 sm:mb-12 text-sm sm:text-base">
          {t('pricing.professionalPrint', { defaultValue: 'Profesionální tisk na prémiovém papíru' })}
        </p>

        <div className="flex overflow-x-auto snap-x snap-mandatory pb-6 gap-4 pr-4">
          {pricing.map((option, idx) => {
            const isSelected = posterSettings.size === option.size &&
              (option.frame === 'hanger'
                ? posterSettings.frame === 'hanger'
                : option.frame
                  ? posterSettings.frame === 'frame'
                  : posterSettings.frame === 'none');
            return (
              <div
                key={idx}
                className={cn(
                  "flex-none w-[85vw] sm:w-[500px] snap-center relative rounded-xl sm:rounded-2xl border-2 transition-all flex flex-row cursor-pointer overflow-hidden backdrop-blur-sm",
                  isSelected
                    ? "border-posterden-red shadow-xl bg-posterden-red"
                    : "border-white/10 hover:border-posterden-red/50 bg-[#1E1E1E]/80 group"
                )}
                onClick={() => {
                  const newSettings: Partial<PosterSettings> = {
                    size: option.size as 'A2' | 'B2',
                    frame: (option.frame === 'hanger' ? 'hanger' : option.frame ? 'frame' : 'none') as FrameType
                  };
                  onPosterSettingsChange(newSettings);
                  if (onAddToCart) onAddToCart();
                }}
              >
                {/* Image Container - Left side */}
                <div className="w-1/3 sm:w-2/5 relative">
                  <img
                    src={option.image}
                    alt={option.label}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>

                {/* Content Container - Right side */}
                <div className="flex-1 p-4 sm:p-5 lg:p-6 flex flex-col min-h-[220px]">

                  {option.popular && (
                    <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-white text-posterden-red px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-md z-10">
                      {t('pricing.mostPopular', { defaultValue: 'Nejpopulárnější' })}
                    </div>
                  )}

                  <div className="text-left mb-2 sm:mb-4" style={isSelected ? { color: '#FFFFFF' } : undefined}>
                    <h3 className={cn(
                      "text-base sm:text-lg lg:text-xl font-bold mb-1 leading-tight font-serif",
                      !isSelected && "text-boho-cream"
                    )} style={isSelected ? { color: '#FFFFFF' } : undefined}>
                      {option.label}
                    </h3>
                    {option.badge && (
                      <p className={cn(
                        "text-[10px] sm:text-xs font-medium mb-1 sm:mb-2 uppercase tracking-wide",
                        !isSelected && "text-posterden-red"
                      )} style={isSelected ? { color: '#FFFFFF', opacity: 0.9 } : undefined}>
                        {option.badge}
                      </p>
                    )}
                    <div className="flex items-center justify-start gap-2 mb-1">
                      {/* Price Swap: Price First, then Base Price */}
                      <span className={cn(
                        "text-xl sm:text-2xl font-bold font-serif",
                        !isSelected && "text-posterden-red"
                      )} style={isSelected ? { color: '#FFFFFF' } : undefined}>
                        {option.price.toFixed(2)}€
                      </span>
                      <span className={cn(
                        "text-sm sm:text-base line-through",
                        !isSelected && "text-gray-400"
                      )} style={isSelected ? { color: '#FFFFFF', opacity: 0.7 } : undefined}>
                        {option.basePrice.toFixed(2)}€
                      </span>
                    </div>
                  </div>

                  <ul className="space-y-1 mb-4 text-xs sm:text-sm flex-grow" style={isSelected ? { color: '#FFFFFF' } : undefined}>
                    {option.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-left">
                        <span className="mt-0.5 min-w-[12px]" style={isSelected ? { color: '#FFFFFF' } : { color: 'var(--posterden-red)' }}>✓</span>
                        <span style={isSelected ? { color: '#FFFFFF' } : { color: 'var(--boho-beige)' }}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    className={cn(
                      "w-full py-2 sm:py-2.5 rounded-lg font-bold transition-all mt-auto text-xs sm:text-sm",
                      isSelected
                        ? "border-2 border-white bg-transparent hover:bg-white/10 text-white"
                        : "bg-posterden-red hover:bg-posterden-dark text-white"
                    )}
                  >
                    {isSelected ? t('pricing.selected', { defaultValue: 'Vybráno' }) : t('pricing.select', { defaultValue: 'Vybrat' })}
                  </button>

                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}


